import i18n, { initI18n } from '@/i18n';
import { MapManager } from './services/MapManager';
import { UIManager } from './services/UIManager';
import './styles/main.scss';
import type { Place } from './types';
import { fetchPlaces } from './utils/api';
import { generateId, validatePlace } from './utils/helpers';

/**
 * Main Application Class
 */
class App {
    private mapManager: MapManager | null = null;
    private uiManager: UIManager;

    constructor() {
        this.uiManager = new UIManager();
        void this.init();
    }

    /**
     * Initialize the application
     */
    private async init(): Promise<void> {
        try {
            // Get Google Maps API key from environment
            const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

            if (!apiKey) {
                throw new Error(i18n.t('errors.missingApiKey'));
            }

            // Initialize map
            const mapElement = document.getElementById('map');
            if (!mapElement) {
                throw new Error(i18n.t('errors.missingMapElement'));
            }

            this.mapManager = new MapManager(apiKey);
            await this.mapManager.initMap(mapElement);

            // Load places data
            await this.loadPlaces();

            // Set up UI callbacks
            this.setupCallbacks();
        } catch (error) {
            console.error('Failed to initialize app:', error);
            this.uiManager.showError(
                error instanceof Error ? error.message : i18n.t('errors.initFailed')
            );
        }
    }

    /**
     * Load places from API
     */
    private async loadPlaces(): Promise<void> {
        this.uiManager.showLoading();

        try {
            const response = await fetchPlaces();

            if (!response.success) {
                throw new Error(response.message || i18n.t('errors.fetchFailed'));
            }

            // Validate and process places
            const validPlaces: Place[] = response.places
                .filter((place): place is Place => validatePlace(place))
                .map((place: Place) => ({
                    ...place,
                    id: place.id || generateId(),
                }));
            if (validPlaces.length === 0) {
                this.uiManager.showError(i18n.t('errors.noValidPlaces'));
                return;
            }

            // Set places in UI
            this.uiManager.setPlaces(validPlaces);
            // this.uiManager.setPlaces(samplePlaces);

            // Add markers to map
            if (this.mapManager) {
                this.mapManager.addMarkers(validPlaces, (place) => {
                    this.uiManager.setActiveItem(place.id);
                });
            }
        } catch (error) {
            console.error('Failed to load places:', error);
            this.uiManager.showError(
                error instanceof Error ? error.message : i18n.t('errors.loadFailed')
            );
        }
    }

    /**
     * Set up UI callbacks
     */
    private setupCallbacks(): void {
        // Handle filter changes
        this.uiManager.onFilterChangeCallback((filteredPlaces) => {
            if (this.mapManager) {
                const visibleIds = new Set(filteredPlaces.map((p) => p.id));
                this.mapManager.filterMarkers(visibleIds);
            }
        });

        // Handle place clicks from list
        this.uiManager.onPlaceClickCallback((place) => {
            if (this.mapManager) {
                this.mapManager.focusOnPlace(place);
            }
        });
    }
}

async function bootstrap(): Promise<void> {
    await initI18n();

    const startApp = () => {
        void new App();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startApp);
    } else {
        startApp();
    }
}

void bootstrap();

// Export for embedding
export { App };

